// =============================================================================
// features/crm/CrmDetailPage.tsx — Tela /crm/:id: detalhe de lead.
//
// DS:
//   - Header: avatar azul + nome Bricolage + meta caption
//   - Timeline lateral: Card com hover Spotlight (halo verde — DS §8)
//   - Dados pessoais em Card agrupado
//   - Status como Badge colorido (DS §9.5)
//   - Botão "Editar": abre drawer inline (coluna direita expande) — decisão de
//     UX: drawer lateral preferido ao modal para edição, pois permite comparar
//     dados existentes com campos editáveis side-by-side sem cobrir a timeline.
//
// LGPD:
//   - Telefone: SEMPRE maskPhone()
//   - Email: truncateEmail() na timeline e em cards
//   - CPF: nunca exibido
//   - Sem console.log(lead)
// =============================================================================

import type { Appointment, LeadUpdate } from '@elemento/shared-schemas';
import type { LeadOriginChannel } from '@elemento/shared-schemas';
import { LEAD_ORIGIN_CHANNEL_LABELS, validateCnpjDigits } from '@elemento/shared-schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useToast } from '../../components/ui/Toast';
import { useAssignableAgents } from '../../hooks/admin/useAgents';
import {
  buildTimelineDisplayItems,
  inferDirection,
  isLivechatInteraction,
  livechatMessageLabel,
  parseLivechatMarker,
} from '../../hooks/crm/livechatTimeline';
import type { LeadInteraction, LeadStatus } from '../../hooks/crm/types';
import {
  STATUS_META,
  SOURCE_LABEL,
  maskPhone,
  truncateEmail,
  formatDate,
  formatRelativeDate,
} from '../../hooks/crm/types';
import { LEAD_QUERY_KEY, useLead, useLeadInteractions } from '../../hooks/crm/useLead';
import {
  useAppointmentsGate,
  useLeadAppointmentsTimeline,
  useLeadNextActions,
} from '../../hooks/crm/useLeadAppointments';
import { useUpdateLead } from '../../hooks/crm/useUpdateLead';
import { useKanbanStages } from '../../hooks/kanban/useKanbanStages';
import { useContactPreferences } from '../../hooks/sales/useContactPreferences';
import {
  useCampaignMembershipGate,
  useLeadCommercialContext,
} from '../../hooks/sales/useLeadCommercialContext';
import { useCitiesList } from '../../hooks/useCitiesList';
import { api, ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatOrgDateTime } from '../../lib/orgDateTime';
import { useAuth } from '../auth/useAuth';
import { StartConversationModal } from '../conversations/components/StartConversationModal';
import { LawFirmReferralButton } from '../customers/components/LawFirmReferralButton';
import { LeadCreditAnalysisTab } from '../leads/components/LeadCreditAnalysisTab';
// Contrato de saída do módulo comercial (F33-S06/F34-S05, features/sales/index.ts)
// — mesmo diálogo/badge da Agenda e do Kanban (doc 26 §22), mesmo formulário
// de registro de contato/bloqueio de qualquer outra superfície.
import {
  BlockContactDialog,
  CampaignMembershipCard,
  ContactAttemptsHistory,
  ContactRestrictionBanner,
  NextActionBadge,
  RegisterContactDialog,
  ScheduleAppointmentDialog,
} from '../sales';

import { CustomerDetailDrawer } from './components/CustomerDetailDrawer';
import { LeadDocumentsTab } from './components/LeadDocumentsTab';
import { LivechatGroupCard } from './components/LivechatGroupCard';
import { SimulationHistory } from './components/SimulationHistory';
import { SpotlightCard } from './components/SpotlightCard';
import { agentDisplayLabel, buildAgentOptions, buildCityOptions } from './editOptions';

// ─── Compromissos da agenda comercial (F33-S07, doc 26 §6.3) ─────────────────
//
// Fuso da organização — fonte única em lib/orgDateTime.ts (F38-S10).

const APPOINTMENT_CHANNEL_LABELS: Record<Appointment['channel'], string> = {
  phone_manual: 'Ligação',
  phone_integrated: 'Ligação (telefonia)',
  whatsapp: 'WhatsApp',
  in_person: 'Presencial',
  other: 'Outro',
};

function formatAppointmentDateTime(iso: string): string {
  return formatOrgDateTime(iso);
}

/**
 * Converte um Appointment em LeadInteraction para a timeline única do lead
 * (doc 26 §6.3 "Linha do tempo única para mensagens, mudanças de estágio,
 * compromissos..."). Nunca uma origem de dado própria — só reformatação do
 * mesmo registro que já veio da API de compromissos.
 */
function appointmentToInteraction(appointment: Appointment, agentName: string): LeadInteraction {
  const channelLabel = APPOINTMENT_CHANNEL_LABELS[appointment.channel];
  const when = formatAppointmentDateTime(appointment.scheduledAt);

  let content: string;
  if (appointment.status === 'cancelled') {
    content = `Retorno cancelado — ${channelLabel}, estava marcado para ${when}.`;
  } else if (appointment.status === 'completed') {
    content = `Retorno concluído — ${channelLabel}, ${when}.`;
  } else {
    content = `Retorno agendado — ${channelLabel}, ${when}.`;
  }
  if (appointment.reason) {
    content += ` Motivo: ${appointment.reason}`;
  }

  return {
    id: `appointment-${appointment.id}`,
    leadId: appointment.leadId,
    type: 'appointment',
    content,
    actorName: agentName,
    createdAt: appointment.scheduledAt,
  };
}

// ─── Card "Próxima ação" (doc 26 §6.3) ───────────────────────────────────────

function NextActionCard({
  appointment,
  agentName,
}: {
  appointment: Appointment;
  agentName: string;
}): React.JSX.Element {
  const now = React.useMemo(() => new Date(), []);
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <h2
          className="font-sans font-bold text-ink-3 uppercase"
          style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
        >
          Próxima ação
        </h2>
        <NextActionBadge appointment={appointment} now={now} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <p
            className="font-sans font-semibold text-ink-3 uppercase mb-1"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Horário
          </p>
          <p className="font-mono text-sm text-ink-2">
            {formatAppointmentDateTime(appointment.scheduledAt)}
          </p>
        </div>
        <div>
          <p
            className="font-sans font-semibold text-ink-3 uppercase mb-1"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Responsável
          </p>
          <p className="font-sans text-sm text-ink-2">{agentName}</p>
        </div>
        <div>
          <p
            className="font-sans font-semibold text-ink-3 uppercase mb-1"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Origem
          </p>
          <p className="font-sans text-sm text-ink-2">
            {appointment.campaignMemberId ? 'Campanha' : 'Direto'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Skeletons ────────────────────────────────────────────────────────────────

function HeaderSkeleton(): React.JSX.Element {
  return (
    <div className="flex items-start gap-4 animate-pulse">
      <div
        className="w-14 h-14 rounded-pill shrink-0"
        style={{ background: 'var(--surface-muted)' }}
      />
      <div className="flex flex-col gap-2 flex-1">
        <div className="h-6 w-48 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
        <div className="h-4 w-64 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
      </div>
    </div>
  );
}

function CardSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5 animate-pulse"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="h-4 w-28 rounded-xs mb-4" style={{ background: 'var(--surface-muted)' }} />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="mb-3">
          <div
            className="h-3 w-20 rounded-xs mb-1.5"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div
            className="h-4 rounded-xs"
            style={{ width: 100 + i * 30, background: 'var(--surface-muted)' }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Status options ───────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'new', label: 'Novo' },
  { value: 'qualifying', label: 'Qualificando' },
  { value: 'simulation', label: 'Simulação' },
  { value: 'closed_won', label: 'Convertido' },
  { value: 'closed_lost', label: 'Perdido' },
  { value: 'archived', label: 'Arquivado' },
];

// ─── Ícone de tipo de interação ───────────────────────────────────────────────

function InteractionIcon({ type }: { type: LeadInteraction['type'] }): React.JSX.Element {
  const iconProps = {
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    className: 'w-4 h-4',
  };

  switch (type) {
    case 'note':
      return (
        <svg {...iconProps}>
          <path d="M6 2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" />
          <path d="M8 8h4M8 12h3" />
        </svg>
      );
    case 'status_change':
      return (
        <svg {...iconProps}>
          <path d="M5 10h10M13 7l3 3-3 3" />
        </svg>
      );
    case 'whatsapp':
      return (
        <svg {...iconProps}>
          <path d="M18 10a8 8 0 1 1-8-8" />
          <path d="M18 2v6h-6" />
          <path d="M7 13s1 2 3 2 3-2 3-2" />
        </svg>
      );
    case 'call':
      return (
        <svg {...iconProps}>
          <path d="M5 4a1 1 0 0 1 1-1h2l2 5-2 1a11 11 0 0 0 5 5l1-2 5 2v2a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1Z" />
        </svg>
      );
    case 'appointment':
      // Compromisso da agenda comercial mesclado na timeline (F33-S07).
      return (
        <svg {...iconProps}>
          <rect x="3" y="4" width="14" height="13" rx="2" />
          <path d="M3 8h14" />
          <path d="M7 2v4M13 2v4" />
        </svg>
      );
    default: // system
      return (
        <svg {...iconProps}>
          <circle cx="10" cy="10" r="8" />
          <path d="M10 7v4M10 14v.5" />
        </svg>
      );
  }
}

const INTERACTION_COLORS: Record<LeadInteraction['type'], string> = {
  note: 'var(--info)',
  status_change: 'var(--brand-azul)',
  whatsapp: 'var(--brand-verde)',
  appointment: 'var(--brand-amarelo)',
  call: 'var(--brand-amarelo)',
  system: 'var(--text-3)',
};

/**
 * Rótulo da contagem no cabeçalho fixo da Timeline (F49-S01) — visível mesmo
 * quando a lista está cortada pelo teto de altura (`max-h-[60vh]`), já que
 * "esconder que há mais" é o anti-padrão que o slot proíbe. `null` quando não
 * há nada a contar (lista vazia já tem seu próprio estado dedicado).
 */
export function formatInteractionsCountLabel(count: number): string | null {
  if (count <= 0) return null;
  return `(${count} ${count === 1 ? 'interação' : 'interações'})`;
}

/**
 * Teto de altura da Timeline (F49-S01) — `max-h`, nunca `h-`, para uma lista
 * curta não virar caixa vazia. `pr-1` evita a barra de rolagem colar no
 * conteúdo (convenção da casa, ver `LeadListCard.tsx`).
 */
export const TIMELINE_SCROLL_CLASS = 'max-h-[60vh] overflow-y-auto pr-1';

// ─── Painel de edição (drawer inline) ────────────────────────────────────────

// --- helpers locais de máscara e validação ---

function maskCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function maskCnpj(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * Valida dígitos verificadores do CPF (algoritmo oficial Receita Federal).
 * Retorna false para CPFs com todos os dígitos iguais.
 */
function validateCpfDigits(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const calc = (seq: string, len: number): number => {
    let s = 0;
    for (let i = 0; i < len; i++) s += Number(seq[i]!) * (len + 1 - i);
    const r = (s * 10) % 11;
    return r >= 10 ? 0 : r;
  };
  return Number(d[9]!) === calc(d, 9) && Number(d[10]!) === calc(d, 10);
}

// --- sub-componente de título de seção ---

function SectionTitle({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p
      className="font-sans font-bold uppercase text-ink-3"
      style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
    >
      {children}
    </p>
  );
}

// --- interface de props ---

interface EditPanelProps {
  leadId: string;
  currentName: string;
  currentEmail: string | null;
  currentPhone: string;
  currentCnpj: string | null;
  currentLegalName: string | null;
  currentAgentId: string | null;
  currentStatus: LeadStatus;
  currentNotes: string | null;
  currentOriginChannel: LeadOriginChannel | null;
  currentDistrict: string | null;
  currentCityId: string | null;
  /** Nome da cidade atual (para rotular "<nome> (inativa)" — CRM-04). */
  currentCityName: string | null;
  onClose: () => void;
}

function EditPanel({
  leadId,
  currentName,
  currentEmail,
  currentPhone,
  currentCnpj,
  currentLegalName,
  currentAgentId,
  currentStatus,
  currentNotes,
  currentOriginChannel,
  currentDistrict,
  currentCityId,
  currentCityName,
  onClose,
}: EditPanelProps): React.JSX.Element {
  const { toast } = useToast();

  // ── Estado de cada campo ─────────────────────────────────────────────────
  const [name, setName] = React.useState(currentName);
  const [email, setEmail] = React.useState(currentEmail ?? '');
  // CPF é write-only: começa sempre vazio (nunca pré-preenchido — LGPD)
  const [cpfDisplay, setCpfDisplay] = React.useState('');
  const [cnpjDisplay, setCnpjDisplay] = React.useState(currentCnpj ? maskCnpj(currentCnpj) : '');
  const [legalName, setLegalName] = React.useState(currentLegalName ?? '');
  const [status, setStatus] = React.useState<LeadStatus>(currentStatus);
  const [cityId, setCityId] = React.useState(currentCityId ?? '');
  const [agentId, setAgentId] = React.useState(currentAgentId ?? '');
  const [notes, setNotes] = React.useState(currentNotes ?? '');
  const [originChannel, setOriginChannel] = React.useState<string>(currentOriginChannel ?? '');
  const [district, setDistrict] = React.useState(currentDistrict ?? '');
  // Seção PJ: aberta por padrão se o lead já tiver dados PJ
  const [showPjSection, setShowPjSection] = React.useState(
    Boolean(currentCnpj || currentLegalName),
  );

  // ── Erros de validação inline ────────────────────────────────────────────
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [cpfError, setCpfError] = React.useState<string | null>(null);
  const [cnpjError, setCnpjError] = React.useState<string | null>(null);

  // ── Dados externos ───────────────────────────────────────────────────────
  const { cities, isLoading: citiesLoading } = useCitiesList();
  // Cidade inativa / agente fora da lista entram como opção rotulada, com o id
  // que o lead já tem — o select não mente "Selecione…" (CRM-04). Payload igual.
  const cityOptions = React.useMemo(
    () => buildCityOptions(cities, { id: currentCityId, name: currentCityName }, citiesLoading),
    [cities, currentCityId, currentCityName, citiesLoading],
  );

  // Usa /api/agents/assignable (gated por leads:write) — não /api/admin/agents,
  // que exige agents:manage e devolve 403 para agentes/operadores (dropdown vazio).
  const { agents: assignableAgents, isLoading: agentsLoading } = useAssignableAgents();
  const agentOptions = React.useMemo(
    () => buildAgentOptions(assignableAgents, currentAgentId, agentsLoading),
    [assignableAgents, currentAgentId, agentsLoading],
  );

  // ── Mutation ─────────────────────────────────────────────────────────────
  const { updateLead, isPending } = useUpdateLead(leadId, {
    onSuccess: () => {
      toast('Lead atualizado!', 'success');
      onClose();
    },
    onError: (msg) => {
      toast(msg, 'danger');
    },
  });

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    // Validação inline antes de montar o payload
    let hasError = false;

    if (!name.trim()) {
      setNameError('O nome é obrigatório.');
      hasError = true;
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('E-mail inválido.');
      hasError = true;
    }

    const cpfDigits = cpfDisplay.replace(/\D/g, '');
    if (cpfDigits && (cpfDigits.length !== 11 || !validateCpfDigits(cpfDigits))) {
      setCpfError('CPF inválido — verifique os dígitos.');
      hasError = true;
    }

    const cnpjDigits = cnpjDisplay.replace(/\D/g, '');
    if (cnpjDigits && !validateCnpjDigits(cnpjDigits)) {
      setCnpjError('CNPJ inválido — dígitos verificadores incorretos.');
      hasError = true;
    }

    if (hasError) return;

    // Constrói payload com apenas os campos que mudaram vs os valores atuais.
    // Isso evita writes desnecessários e respeita a regra "pelo menos 1 campo".
    const payload: LeadUpdate = {};

    if (name.trim() !== currentName.trim()) {
      payload.name = name.trim();
    }

    const emailNorm = email.trim() || null;
    if (emailNorm !== (currentEmail ?? null)) {
      payload.email = emailNorm;
    }

    // CPF é write-only: só inclui se o usuário preencheu um CPF completo e válido.
    if (cpfDigits.length === 11) {
      payload.cpf = cpfDisplay; // schema aceita com máscara (000.000.000-00)
    }

    const currentCnpjDigits = (currentCnpj ?? '').replace(/\D/g, '');
    if (cnpjDigits !== currentCnpjDigits) {
      payload.cnpj = cnpjDigits || null;
    }

    const legalNameNorm = legalName.trim() || null;
    if (legalNameNorm !== (currentLegalName ?? null)) {
      payload.legal_name = legalNameNorm;
    }

    if (status !== currentStatus) {
      payload.status = status;
    }

    // city_id: a API aceita transferência; vazio = não alterar
    if (cityId !== (currentCityId ?? '')) {
      if (cityId) payload.city_id = cityId;
    }

    const agentIdNorm = agentId || null;
    if (agentIdNorm !== (currentAgentId ?? null)) {
      payload.agent_id = agentIdNorm;
    }

    const notesNorm = notes.trim() || null;
    const districtNorm = district.trim() || null;
    const originNorm = originChannel || null;
    if (notesNorm !== (currentNotes ?? null)) {
      payload.notes = notesNorm;
    }

    if (originNorm !== (currentOriginChannel ?? null)) {
      payload.origin_channel = originNorm as typeof payload.origin_channel;
    }

    if (districtNorm !== (currentDistrict ?? null)) {
      payload.district = districtNorm;
    }

    if (Object.keys(payload).length === 0) {
      toast('Nenhuma alteração foi detectada.', 'info');
      return;
    }

    updateLead(payload);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-3)' }}
    >
      {/* Cabeçalho do painel */}
      <div className="flex items-center justify-between mb-5">
        <h3
          className="font-display font-bold text-ink"
          style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
        >
          Editar lead
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar edição"
          className="w-7 h-7 flex items-center justify-center rounded-xs text-ink-3 hover:text-ink hover:bg-surface-hover transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20"
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            className="w-4 h-4"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        {/* ── 1. Contato ─────────────────────────────────────────────────── */}
        <section className="flex flex-col gap-3" aria-label="Contato">
          <SectionTitle>Contato</SectionTitle>

          <Input
            id="edit-name"
            label="Nome"
            required
            placeholder="Nome completo"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            error={nameError ?? undefined}
          />

          <Input
            id="edit-email"
            label="E-mail"
            type="email"
            placeholder="email@exemplo.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            error={emailError ?? undefined}
          />

          {/* Telefone — imutável por design; exibido mascarado (LGPD) */}
          <Input
            id="edit-phone"
            label="Telefone"
            value={maskPhone(currentPhone)}
            readOnly
            disabled
            className="font-mono"
            hint="O telefone não pode ser alterado após a criação."
          />

          {/* CPF — write-only (LGPD: nunca exibido, apenas atualizado) */}
          <Input
            id="edit-cpf"
            label="CPF"
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={cpfDisplay}
            onChange={(e) => {
              setCpfDisplay(maskCpf(e.target.value));
              if (cpfError) setCpfError(null);
            }}
            error={cpfError ?? undefined}
            hint="Nunca exibido por proteção de dados (LGPD). Preencha apenas para atualizar."
          />
        </section>

        {/* ── 2. Empresa (PJ) ────────────────────────────────────────────── */}
        <section className="flex flex-col gap-2" aria-label="Empresa">
          <button
            type="button"
            onClick={() => setShowPjSection((v) => !v)}
            aria-expanded={showPjSection}
            aria-controls="edit-pj-section"
            className={cn(
              'flex items-center gap-1.5 w-fit',
              'font-sans font-bold uppercase text-ink-3 hover:text-ink',
              'transition-colors duration-fast ease',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20 rounded-sm',
            )}
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              className={cn(
                'w-3 h-3 transition-transform duration-150',
                showPjSection && 'rotate-90',
              )}
              aria-hidden="true"
            >
              <path d="M6 4l4 4-4 4" />
            </svg>
            Empresa (opcional)
          </button>

          <div
            id="edit-pj-section"
            hidden={!showPjSection}
            className="flex flex-col gap-3 rounded-md border border-border-subtle bg-surface-2 px-4 py-4"
            style={{ boxShadow: 'var(--elev-1)' }}
          >
            <p className="font-sans text-xs text-ink-3 leading-relaxed">
              Preencha apenas se o lead representar uma empresa. Deixe em branco para remover.
            </p>

            <Input
              id="edit-cnpj"
              label="CNPJ"
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
              value={cnpjDisplay}
              onChange={(e) => {
                setCnpjDisplay(maskCnpj(e.target.value));
                if (cnpjError) setCnpjError(null);
              }}
              error={cnpjError ?? undefined}
            />

            <Input
              id="edit-legal-name"
              label="Razão social"
              placeholder="Ex: Comercial Silva Ltda"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
            />
          </div>
        </section>

        {/* ── 3. Atendimento ─────────────────────────────────────────────── */}
        <section className="flex flex-col gap-3" aria-label="Atendimento">
          <SectionTitle>Atendimento</SectionTitle>

          <Select
            id="edit-status"
            label="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(e) => setStatus(e.target.value as LeadStatus)}
          />

          <Select
            id="edit-city"
            label="Cidade"
            options={cityOptions}
            value={cityId}
            onChange={(e) => setCityId(e.target.value)}
            hint="Necessária para simular crédito (a regra do produto é por cidade)."
          />

          <Select
            id="edit-agent"
            label="Agente responsável"
            options={agentOptions}
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            disabled={agentsLoading}
          />
        </section>

        {/* ── 4. Informações complementares ──────────────────────────────── */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            id="edit-origin-channel"
            label="Como conheceu o Banco do Povo"
            options={[
              { value: '', label: 'Não informado' },
              ...Object.entries(LEAD_ORIGIN_CHANNEL_LABELS).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
            value={originChannel}
            onChange={(e) => setOriginChannel(e.target.value)}
          />
          <Input
            id="edit-district"
            label="Distrito"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            placeholder="Ex: Distrito 1"
          />
        </section>

        {/* ── 5. Notas ───────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="edit-notes"
            className="font-sans text-xs font-semibold text-ink-2 uppercase tracking-[0.1em]"
          >
            Notas
          </label>
          <textarea
            id="edit-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observações sobre o lead..."
            className={cn(
              'w-full font-sans text-sm font-medium text-ink',
              'bg-surface-1 rounded-sm px-[14px] py-[11px]',
              'border border-border-strong',
              'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
              'transition-[border-color,box-shadow] duration-fast ease',
              'placeholder:text-ink-4',
              'hover:border-ink-3',
              'focus:outline-none focus:border-azul',
              'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)]',
              'resize-none',
            )}
          />
        </div>

        {/* ── Ações ──────────────────────────────────────────────────────── */}
        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isPending}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={isPending} className="flex-1">
            {isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * CrmDetailPage — /crm/:id
 * Layout de 2 colunas: dados principais (esq) + timeline (dir).
 */
export function CrmDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const leadId = id ?? '';
  const navigate = useNavigate();

  const { lead, isLoading, isError } = useLead(leadId);
  const { interactions, isLoading: loadingInteractions } = useLeadInteractions(leadId);
  const [editOpen, setEditOpen] = React.useState(false);
  // Ficha do cliente — abre quando o lead foi convertido (closed_won)
  const [customerDrawerOpen, setCustomerDrawerOpen] = React.useState(false);

  // RBAC: apenas usuários com kanban:move podem mudar o estágio pelo CRM (F18-S07).
  const { hasPermission, user } = useAuth();
  const canMoveKanbanStage = hasPermission('kanban:move');
  // Editar o lead exige `leads:write` (PERM-08): sem ela o botão nem existe —
  // a API negaria com 403 e a tela não oferece o que não pode ser usado.
  const canWriteLeads = hasPermission('leads:write');

  // "Iniciar conversa, escolhendo a caixa" (F43-S11) — mesma permissão que o
  // backend exige em canais-disponiveis/iniciar (`livechat:message:send`,
  // ver channel-picker.schema.ts). Sem gate de flag: o endpoint novo (F43-S10)
  // não checa nenhuma — RBAC + escopo de cidade já bastam. Substitui o fluxo
  // antigo de F41-S02 (só Meta, só template, atrás de flag DARK que nunca foi
  // seedada) — a tela nova cobre o mesmo caso e mais (caixa pessoal do
  // vendedor, texto livre dentro da janela).
  const canStartConversation = hasPermission('livechat:message:send');
  const [startConversationOpen, setStartConversationOpen] = React.useState(false);

  // Mudança de estágio de Kanban a partir da ficha (F13-S03) — reusa o move do board.
  const { stages: kanbanStages } = useKanbanStages();
  const { toast: detailToast } = useToast();
  const queryClient = useQueryClient();
  const moveStageMutation = useMutation({
    mutationFn: ({ cardId, toStageId }: { cardId: string; toStageId: string }) =>
      api.post(`/api/kanban/cards/${cardId}/move`, { toStageId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEAD_QUERY_KEY(leadId) });
      detailToast('Estágio atualizado.', 'success');
    },
    onError: (err: Error) => {
      const msg =
        err instanceof ApiError && err.status === 422
          ? 'Transição de estágio não permitida.'
          : 'Erro ao mudar o estágio.';
      detailToast(msg, 'danger');
    },
  });

  // ── Agenda comercial (F33-S07, doc 26 §6.3) ──────────────────────────────
  // Gate único: com a permissão/flag fechada, nenhum hook abaixo dispara
  // requisição e nada renderiza — ficha idêntica à de hoje (DoD do slot).
  const appointmentsGate = useAppointmentsGate();
  const [scheduleDialogOpen, setScheduleDialogOpen] = React.useState(false);
  const { agents: assignableAgents, isLoading: agentsLoading } = useAssignableAgents();

  // ── Registro de contato + restrição (F34-S05, doc 26 §6.3/§8) ────────────
  // Mesmo gate de permissão/flag de compromissos (`sales_appointments:read`/
  // `write` + `sales.appointments.enabled`) — attempts/preferences reusam a
  // MESMA dupla (ver header de apps/api/.../attempts/routes.ts e
  // .../preferences/routes.ts, "nenhuma flag/permissão nova neste módulo").
  const [registerContactOpen, setRegisterContactOpen] = React.useState(false);
  const [blockContactOpen, setBlockContactOpen] = React.useState(false);
  const {
    preferences: contactPreferences,
    blockedChannels,
    blocked: blockedPreferences,
    isFullyBlocked,
  } = useContactPreferences(leadId, { enabled: appointmentsGate.canRead });
  const resolveAgentName = React.useCallback(
    (agentId: string): string =>
      assignableAgents.find((a) => a.id === agentId)?.display_name ?? 'Sem responsável',
    [assignableAgents],
  );

  const { nextActionByLead } = useLeadNextActions([leadId], {
    enabled: appointmentsGate.canRead && leadId.length > 0,
  });
  const nextAppointment = nextActionByLead.get(leadId);

  // ── Bloco Campanha (F38-S06, doc planejamento-livechat-contexto-comercial.md
  // §3-B) ────────────────────────────────────────────────────────────────
  // Gate próprio, fail-closed: `livechat.commercial_context.enabled` +
  // `sales.campaigns.enabled` + `sales_campaigns:read` — com qualquer um dos
  // três ausente, `campaignGate.canRead` é `false`, o hook abaixo não
  // dispara nenhum request e `CampaignMembershipCard` recebe `campaign: null`
  // (nada no DOM).
  const campaignGate = useCampaignMembershipGate();
  const { context: commercialContext, isLoading: commercialContextLoading } =
    useLeadCommercialContext(leadId, { enabled: campaignGate.canRead });

  const { appointments: appointmentsTimeline } = useLeadAppointmentsTimeline(leadId, {
    enabled: appointmentsGate.canRead,
  });
  // Timeline única (doc 26 §6.3): mescla compromissos às interações
  // existentes, ordenado do mais recente para o mais antigo.
  const mergedInteractions = React.useMemo(() => {
    if (appointmentsTimeline.length === 0) return interactions;
    const appointmentInteractions = appointmentsTimeline.map((a) =>
      appointmentToInteraction(a, resolveAgentName(a.assignedAgentId)),
    );
    return [...interactions, ...appointmentInteractions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [interactions, appointmentsTimeline, resolveAgentName]);

  // F49-S03: nunca renderizar o marcador técnico "[livechat] type=... ref=..."
  // cru — e agrupar mensagens consecutivas do live chat (mesmo dia civil) num
  // único card, senão uma conversa de 20 mensagens vira 20 cards idênticos.
  // Ver hooks/crm/livechatTimeline.ts para as regras e limitações conhecidas.
  const timelineDisplayItems = React.useMemo(
    () => buildTimelineDisplayItems(mergedInteractions),
    [mergedInteractions],
  );

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="font-sans text-sm text-danger">Erro ao carregar lead.</p>
        <Link to="/crm">
          <Button variant="outline">← Voltar para CRM</Button>
        </Link>
      </div>
    );
  }

  // LGPD: mascarar PII antes de qualquer uso
  const phoneMasked = lead ? maskPhone(lead.phone_e164) : null;
  const emailTrunc = lead?.email ? truncateEmail(lead.email) : null;
  const statusMeta = lead ? STATUS_META[lead.status] : null;

  return (
    <>
      <div
        className="flex flex-col gap-6"
        style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) both' }}
      >
        {/* Breadcrumb + voltar */}
        <div className="flex items-center gap-2">
          <Link
            to="/crm"
            className="font-sans text-sm text-ink-3 hover:text-azul transition-colors flex items-center gap-1"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
            >
              <path d="M10 4l-4 4 4 4" />
            </svg>
            CRM
          </Link>
          <span className="text-ink-4 text-sm">/</span>
          <span className="font-sans text-sm text-ink truncate max-w-xs">
            {isLoading ? '...' : (lead?.name ?? 'Lead')}
          </span>
        </div>

        {/* Banner de restrição de contato (F34-S05, doc 26 §6.3/§22) — texto
            SEMPRE presente (não só cor); desabilita os botões de contato
            abaixo quando `isFullyBlocked`. */}
        {appointmentsGate.canRead && blockedPreferences.length > 0 && (
          <ContactRestrictionBanner
            id="crm-detail-contact-restriction"
            blocked={blockedPreferences}
            isFullyBlocked={isFullyBlocked}
          />
        )}

        {/* Header do lead */}
        {isLoading ? (
          <HeaderSkeleton />
        ) : lead ? (
          <div
            className="flex items-start justify-between gap-4"
            style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) 0.05s both' }}
          >
            <div className="flex items-center gap-4">
              {/* Avatar variante azul conforme spec */}
              <Avatar name={lead.name} variant="azul" size="lg" />
              <div>
                <h1
                  className="font-display font-bold text-ink"
                  style={{
                    fontSize: 'var(--text-2xl)',
                    letterSpacing: '-0.035em',
                    fontVariationSettings: "'opsz' 32",
                  }}
                >
                  {lead.name}
                </h1>
                <div className="flex items-center flex-wrap gap-2 mt-1">
                  {statusMeta && <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>}
                  <span
                    className="font-sans text-xs text-ink-3 uppercase font-semibold"
                    style={{ letterSpacing: '0.1em' }}
                  >
                    {SOURCE_LABEL[lead.source] ?? lead.source}
                  </span>
                  <span className="text-ink-4 text-xs">Criado {formatDate(lead.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Ficha do cliente — disponível somente quando convertido E customer_id existe */}
              {lead.status === 'closed_won' && lead.customer_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCustomerDrawerOpen(true)}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <rect x="2" y="3" width="12" height="10" rx="2" />
                      <path d="M5 7h6M5 10h4" />
                    </svg>
                  }
                >
                  Ver ficha do cliente
                </Button>
              )}

              {/* Agendar retorno (F33-S07, doc 26 §6.3) — gate de escrita
                  (permissão + flag); sem os dois, o botão não existe. Fica
                  desabilitado (nunca escondido) quando `isFullyBlocked`
                  (F34-S05, doc 26 §22: "o bloqueio é evidente e respeitado
                  por todos os botões") — o motivo já está no banner acima. */}
              {appointmentsGate.canWrite && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScheduleDialogOpen(true)}
                  disabled={isFullyBlocked}
                  title={isFullyBlocked ? 'Contato bloqueado — veja o aviso acima' : undefined}
                  aria-describedby={isFullyBlocked ? 'crm-detail-contact-restriction' : undefined}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
                      <path d="M2.5 6.5h11" />
                      <path d="M5.5 2v3M10.5 2v3" />
                    </svg>
                  }
                >
                  Agendar retorno
                </Button>
              )}

              {/* Registrar contato (F34-S05, doc 26 §6.3) — mesmo gate de
                  escrita de "Agendar retorno" (attempts reusa a mesma
                  permissão/flag, ver header de sales/attempts/routes.ts). */}
              {appointmentsGate.canWrite && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRegisterContactOpen(true)}
                  disabled={isFullyBlocked}
                  title={isFullyBlocked ? 'Contato bloqueado — veja o aviso acima' : undefined}
                  aria-describedby={isFullyBlocked ? 'crm-detail-contact-restriction' : undefined}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <path d="M2.5 4a1.5 1.5 0 0 1 1.5-1.5h3l1.5 3.5-1.5.8a7.5 7.5 0 0 0 3.5 3.5l.8-1.5 3.5 1.5v3A1.5 1.5 0 0 1 13.3 15 11 11 0 0 1 2.5 4.7Z" />
                    </svg>
                  }
                >
                  Registrar contato
                </Button>
              )}

              {/* Gerenciar contato (bloqueio/desbloqueio, F34-S05, doc 26 §6.3
                  "Formulário de bloqueio/desbloqueio... para quem tem
                  permissão de escrita, com confirmação de impacto"). */}
              {appointmentsGate.canWrite && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBlockContactOpen(true)}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <circle cx="8" cy="8" r="6" />
                      <path d="M4.5 4.5l7 7" />
                    </svg>
                  }
                >
                  Gerenciar contato
                </Button>
              )}

              {/* Iniciar conversa, escolhendo a caixa (F43-S11) — gate por
                  permissão apenas (mesmo padrão do backend); sem ela, o botão
                  nem existe (mesmo padrão dos botões de agenda/registro de
                  contato acima). */}
              {canStartConversation && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStartConversationOpen(true)}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <path d="M2 8a6 6 0 1 1 3 5.2L2 14l0.8-3A6 6 0 0 1 2 8Z" />
                    </svg>
                  }
                >
                  Iniciar conversa
                </Button>
              )}

              {canWriteLeads && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditOpen((v) => !v)}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      className="w-4 h-4"
                    >
                      <path d="M11 3l2 2-8 8H3v-2l8-8Z" />
                    </svg>
                  }
                >
                  Editar
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {/* Layout 2 colunas */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5"
          style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) 0.1s both' }}
        >
          {/* ── Coluna esquerda: dados + edição ─────────────────────────────── */}
          <div className="flex flex-col gap-5">
            {/* Card dados pessoais */}
            {isLoading ? (
              <CardSkeleton />
            ) : lead ? (
              <SpotlightCard>
                <div className="p-5">
                  <h2
                    className="font-sans font-bold text-ink-3 uppercase mb-4"
                    style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
                  >
                    Dados de contato
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Telefone — LGPD mascarado */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Telefone
                      </p>
                      <p
                        className="font-mono text-ink-2"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.875rem',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {phoneMasked}
                      </p>
                    </div>

                    {/* Email — LGPD truncado */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        E-mail
                      </p>
                      <p className="font-sans text-sm text-ink-2">
                        {emailTrunc ?? <span className="text-ink-4">—</span>}
                      </p>
                    </div>

                    {/* Status de atendimento */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Status de atendimento
                      </p>
                      {statusMeta && <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>}
                    </div>

                    {/* Cidade (F13-S03) */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Cidade
                      </p>
                      <p className="font-sans text-sm text-ink-2">
                        {lead.city_name ?? <span className="text-ink-4">—</span>}
                      </p>
                    </div>

                    {/* Agente responsável (CRM-07) */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Agente responsável
                      </p>
                      <p
                        className={cn(
                          'font-sans text-sm',
                          lead.agent_id ? 'text-ink-2' : 'text-ink-4',
                        )}
                      >
                        {agentDisplayLabel(lead.agent_id, assignableAgents, agentsLoading) ?? '…'}
                      </p>
                    </div>

                    {/* Estágio de Kanban — gestão interna, editável pela ficha (F13-S03/F18-S07) */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Estágio de fluxo interno
                      </p>
                      {lead.kanban_card_id ? (
                        canMoveKanbanStage ? (
                          <Select
                            id="lead-kanban-stage"
                            value={lead.kanban_stage?.id ?? ''}
                            options={kanbanStages.map((s) => ({ value: s.id, label: s.name }))}
                            disabled={moveStageMutation.isPending}
                            onChange={(e) => {
                              const toStageId = e.target.value;
                              const cardId = lead.kanban_card_id;
                              if (cardId && toStageId && toStageId !== lead.kanban_stage?.id) {
                                moveStageMutation.mutate({ cardId, toStageId });
                              }
                            }}
                          />
                        ) : (
                          /* Leitura apenas — sem permissão kanban:move */
                          <Badge variant="neutral">{lead.kanban_stage?.name ?? '—'}</Badge>
                        )
                      ) : (
                        <p className="font-sans text-sm text-ink-4">Sem card no board</p>
                      )}
                    </div>

                    {/* Canal */}
                    <div>
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-1"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Entrada no sistema
                      </p>
                      <p className="font-sans text-sm text-ink-2">
                        {SOURCE_LABEL[lead.source] ?? lead.source}
                      </p>
                    </div>
                  </div>

                  {/* Informações complementares — preenchidas pela IA na
                      qualificação e corrigíveis pelo atendente. */}
                  {(lead.origin_channel || lead.district) && (
                    <div className="mt-4 pt-4 border-t border-border-subtle grid grid-cols-2 gap-4">
                      {lead.origin_channel && (
                        <div>
                          <p
                            className="font-sans font-semibold text-ink-3 uppercase mb-1"
                            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                          >
                            Como conheceu
                          </p>
                          <p className="font-sans text-sm text-ink-2">
                            {LEAD_ORIGIN_CHANNEL_LABELS[lead.origin_channel]}
                          </p>
                        </div>
                      )}
                      {lead.district && (
                        <div>
                          <p
                            className="font-sans font-semibold text-ink-3 uppercase mb-1"
                            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                          >
                            Distrito
                          </p>
                          <p className="font-sans text-sm text-ink-2">{lead.district}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notas */}
                  {lead.notes && (
                    <div className="mt-4 pt-4 border-t border-border-subtle">
                      <p
                        className="font-sans font-semibold text-ink-3 uppercase mb-2"
                        style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                      >
                        Notas
                      </p>
                      <p className="font-sans text-sm text-ink-2 leading-relaxed">{lead.notes}</p>
                    </div>
                  )}
                </div>
              </SpotlightCard>
            ) : null}

            {/* Painel de edição inline (drawer-like) */}
            {editOpen && lead && canWriteLeads && (
              <EditPanel
                leadId={lead.id}
                currentName={lead.name}
                currentEmail={lead.email}
                currentPhone={lead.phone_e164}
                currentCnpj={lead.cnpj}
                currentLegalName={lead.legal_name}
                currentAgentId={lead.agent_id}
                currentStatus={lead.status}
                currentNotes={lead.notes}
                currentOriginChannel={lead.origin_channel}
                currentDistrict={lead.district}
                currentCityId={lead.city_id}
                currentCityName={lead.city_name}
                onClose={() => setEditOpen(false)}
              />
            )}

            {/* Ação: encaminhar para advocacia (F19-S05) — visível quando convertido */}
            {lead && lead.status === 'closed_won' && lead.customer_id && (
              <LawFirmReferralButton customerId={lead.customer_id} customerName={lead.name} />
            )}

            {/* Histórico de tentativas de contato (F34-S05, doc 26 §6.3) —
                gate de leitura; sem tentativa registrada, não renderiza nada. */}
            <ContactAttemptsHistory leadId={leadId} enabled={appointmentsGate.canRead} />

            {/* Histórico de simulações de crédito (F2-S08 / F14-S06) */}
            {/* F14-S06: phone_e164 passado para gating do botão "Enviar ao cliente" */}
            <SimulationHistory leadId={leadId} leadPhone={lead?.phone_e164 ?? null} />

            {/* Tab de análise de crédito (F4-S03) */}
            <LeadCreditAnalysisTab leadId={leadId} />

            {/* Central de documentos do lead (F46-S09) — some sozinha com a
                flag `documents.enabled` OFF ou sem `documents:read`. */}
            <LeadDocumentsTab leadId={leadId} />
          </div>

          {/* ── Coluna direita: próxima ação + timeline ─────────────────────── */}
          <div className="flex flex-col gap-3">
            {/* Card "Próxima ação" (F33-S07, doc 26 §6.3) — acima da timeline,
                gate de leitura; sem compromisso agendado, não renderiza nada. */}
            {appointmentsGate.canRead && nextAppointment && (
              <NextActionCard
                appointment={nextAppointment}
                agentName={resolveAgentName(nextAppointment.assignedAgentId)}
              />
            )}

            {/* Card "Campanha" (F38-S06, doc
                planejamento-livechat-contexto-comercial.md §3-B) — compartilhado
                com o live chat (F38-S04). Sem campanha ativa (ou gate fechado),
                `CampaignMembershipCard` renderiza `null`. */}
            {campaignGate.canRead && (
              <CampaignMembershipCard
                campaign={commercialContext?.campaign ?? null}
                isLoading={commercialContextLoading}
                currentUserId={user?.id ?? null}
                canOperate={campaignGate.canOperate}
              />
            )}

            <h2
              className="font-sans font-bold text-ink-3 uppercase"
              style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
            >
              Timeline
              {!loadingInteractions && (
                <span className="normal-case font-medium text-ink-2 ml-1.5">
                  {formatInteractionsCountLabel(mergedInteractions.length)}
                </span>
              )}
            </h2>

            {loadingInteractions ? (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-md border border-border bg-surface-1 p-4 animate-pulse"
                    style={{ boxShadow: 'var(--elev-1)' }}
                  >
                    <div
                      className="h-3 w-20 rounded-xs mb-2"
                      style={{ background: 'var(--surface-muted)' }}
                    />
                    <div
                      className="h-4 w-full rounded-xs mb-1.5"
                      style={{ background: 'var(--surface-muted)' }}
                    />
                    <div
                      className="h-4 w-3/4 rounded-xs"
                      style={{ background: 'var(--surface-muted)' }}
                    />
                  </div>
                ))}
              </div>
            ) : mergedInteractions.length === 0 ? (
              <SpotlightCard className="p-4">
                <p className="font-sans text-sm text-ink-3 text-center py-4">
                  Nenhuma interação registrada.
                </p>
              </SpotlightCard>
            ) : (
              <div className={cn('flex flex-col gap-3', TIMELINE_SCROLL_CLASS)}>
                {timelineDisplayItems.map((item, idx) => {
                  if (item.kind === 'livechat-group') {
                    return (
                      <div
                        key={item.id}
                        style={{ animationDelay: `${idx * 40}ms` } as React.CSSProperties}
                      >
                        <LivechatGroupCard item={item} />
                      </div>
                    );
                  }

                  const { interaction } = item;
                  // F49-S03: `content` só é exibido cru quando NÃO é a ponte
                  // livechat→CRM — essa grava "[livechat] type=... ref=..."
                  // de propósito (sem PII, doc 17 §8.5), nunca texto pronto
                  // para o operador. Traduz em rótulo humano por
                  // direção+message_type; desconhecido cai no genérico, nunca
                  // no marcador cru.
                  const livechatMarker = isLivechatInteraction(interaction)
                    ? parseLivechatMarker(interaction.content)
                    : null;
                  const displayContent =
                    livechatMarker !== null
                      ? livechatMessageLabel(
                          livechatMarker.messageType,
                          inferDirection(interaction),
                        )
                      : interaction.content;

                  return (
                    <div
                      key={interaction.id}
                      style={{ animationDelay: `${idx * 40}ms` } as React.CSSProperties}
                    >
                      <SpotlightCard className="p-4">
                        {/* Tipo + timestamp */}
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className="flex items-center gap-1.5 font-sans font-semibold text-xs uppercase"
                            style={{
                              color: INTERACTION_COLORS[interaction.type],
                              letterSpacing: '0.08em',
                            }}
                          >
                            <InteractionIcon type={interaction.type} />
                            {interaction.type === 'note' && 'Nota'}
                            {interaction.type === 'status_change' && 'Status'}
                            {interaction.type === 'whatsapp' && 'WhatsApp'}
                            {interaction.type === 'call' && 'Ligação'}
                            {interaction.type === 'appointment' && 'Compromisso'}
                            {interaction.type === 'system' && 'Sistema'}
                          </span>
                          <span className="font-sans text-xs text-ink-4">
                            {formatRelativeDate(interaction.createdAt)}
                          </span>
                        </div>

                        {/* Conteúdo */}
                        <p className="font-sans text-sm text-ink-2 leading-relaxed">
                          {displayContent}
                        </p>

                        {/* Ator */}
                        <p className="font-sans text-xs text-ink-4 mt-2">
                          por {interaction.actorName}
                        </p>

                        {/* Link para o live chat (F49-S03) — acesso controlado
                            e auditado; a mensagem real nunca aparece aqui. */}
                        {livechatMarker !== null && (
                          <div className="mt-1 -ml-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate('/conversas')}
                              className="text-azul"
                            >
                              Ver no atendimento
                            </Button>
                          </div>
                        )}
                      </SpotlightCard>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drawer: ficha consolidada do cliente (contratos, boletos, SPC) */}
      {customerDrawerOpen && lead && lead.status === 'closed_won' && lead.customer_id && (
        <CustomerDetailDrawer
          customerId={lead.customer_id}
          customerName={lead.name}
          onClose={() => setCustomerDrawerOpen(false)}
        />
      )}

      {/* Diálogo de agendamento (F33-S07) — contrato de saída do F33-S06,
          nenhum formulário próprio recriado aqui. */}
      {scheduleDialogOpen && (
        <ScheduleAppointmentDialog
          leadId={leadId}
          {...(lead?.name ? { leadName: lead.name } : {})}
          onClose={() => setScheduleDialogOpen(false)}
          onScheduled={() => {
            setScheduleDialogOpen(false);
            // Atualiza o card "Próxima ação" e a timeline SEM reload — chaves
            // próprias deste slot (hooks/crm/useLeadAppointments.ts), não as
            // de hooks/sales/useAppointments.ts (fora de `files_allowed`).
            void queryClient.invalidateQueries({ queryKey: ['crm', 'lead-next-actions'] });
            void queryClient.invalidateQueries({
              queryKey: ['crm', 'lead-appointments-timeline', leadId],
            });
            void queryClient.invalidateQueries({ queryKey: ['crm', 'due-lead-ids'] });
          }}
        />
      )}

      {/* Diálogo de registro de contato (F34-S05) — contrato de saída de
          features/sales/contacts/, nenhum formulário próprio recriado aqui.
          Só abre quando o gate permite (botão que dispara já fica desabilitado
          com `isFullyBlocked`, ver header dos botões acima). */}
      {registerContactOpen && (
        <RegisterContactDialog
          leadId={leadId}
          {...(lead?.name ? { leadName: lead.name } : {})}
          blockedChannels={blockedChannels}
          origin="manual"
          onClose={() => setRegisterContactOpen(false)}
          onRegistered={() => {
            setRegisterContactOpen(false);
            detailToast('Contato registrado.', 'success');
          }}
        />
      )}

      {/* Diálogo "Iniciar conversa" (F43-S11) — escolha da caixa (oficial ou
          pessoal do vendedor), mesmo componente usado no live chat. Só monta
          quando aberto E o lead já carregou (precisa do nome real). */}
      {startConversationOpen && lead && (
        <StartConversationModal
          leadId={lead.id}
          leadName={lead.name}
          onClose={() => setStartConversationOpen(false)}
          onStarted={(response) => {
            setStartConversationOpen(false);
            detailToast(
              response.conversationCreated
                ? 'Conversa iniciada — mensagem enviada.'
                : 'Este contato já tinha uma conversa — mensagem enviada nela.',
              'success',
            );
            navigate(`/conversas?conversation=${encodeURIComponent(response.conversationId)}`);
          }}
        />
      )}

      {/* Diálogo de bloqueio/desbloqueio de contato (F34-S05, doc 26 §6.3) */}
      {blockContactOpen && (
        <BlockContactDialog
          leadId={leadId}
          {...(lead?.name ? { leadName: lead.name } : {})}
          preferences={contactPreferences}
          onClose={() => setBlockContactOpen(false)}
          onChanged={() => {
            setBlockContactOpen(false);
            detailToast('Preferência de contato atualizada.', 'success');
          }}
        />
      )}
    </>
  );
}
